import { useState } from "react";
import { v4 as uuidv4 } from "uuid";

const useChatTabs = () => {
  const [activeTab, setActiveTab] = useState('default');
  const [chatContexts, setChatContexts] = useState([]);
  const [currentChatContextId, setCurrentChatContextId] = useState(null);

  // Handle tab changes
  const handleTabChange = (event, newValue, onNewTabClick) => {
    // Don't switch tabs for the 'new' tab - it should only trigger creation
    if (newValue === 'new') {
      return; // Don't change active tab, just let the + button handle creation
    } else {
      setActiveTab(newValue);
      setCurrentChatContextId(newValue === 'default' ? null : newValue);
    }
  };

  // Handle creating new tab directly (+ button click)
  const handleCreateNewTab = (onContextCreated) => {
    const newTabId = uuidv4();
    const newContext = {
      id: newTabId,
      name: `Chat ${chatContexts.length + 1}`,
      created: new Date().toISOString(),
    };

    setCurrentChatContextId(newContext.id);
    setChatContexts(prev => [newContext, ...prev]); // Add to beginning instead of end
    setActiveTab(newContext.id);
    
    if (onContextCreated) {
      onContextCreated(newContext);
    }

    return newContext;
  };

  // Handle creating new chat context (from context manager)
  const handleCreateChatContext = (newContext, onContextCreated) => {
    setCurrentChatContextId(newContext.id);
    setChatContexts(prev => [...prev, newContext]); // Add to end instead of beginning
    setActiveTab(newContext.id);
    
    if (onContextCreated) {
      onContextCreated(newContext);
    }
  };

  // Handle updating chat context
  const handleUpdateChatContext = (contextId, newName) => {
    setChatContexts(prev => prev.map(ctx => 
      ctx.id === contextId ? { ...ctx, name: newName } : ctx
    ));
  };

  // Handle editing tab name
  const handleEditTab = (contextId, newName) => {
    handleUpdateChatContext(contextId, newName);
  };

  // Handle deleting chat context
  const handleDeleteChatContext = (contextId) => {
    setChatContexts(prev => prev.filter(ctx => ctx.id !== contextId));
    
    if (currentChatContextId === contextId) {
      const remainingContexts = chatContexts.filter(ctx => ctx.id !== contextId);
      if (remainingContexts.length > 0) {
        setCurrentChatContextId(remainingContexts[0].id);
        setActiveTab(remainingContexts[0].id);
      } else {
        setCurrentChatContextId(null);
        setActiveTab('default');
      }
    }
  };

  // Handle tab close (X button) - now handled by delete in menu
  const handleCloseTab = (event, contextId) => {
    event.stopPropagation();
    handleDeleteChatContext(contextId);
  };

  // Handle deleting tab from menu
  const handleDeleteTab = (contextId) => {
    handleDeleteChatContext(contextId);
  };

  // Handle selecting chat context
  const handleSelectChatContext = (contextId) => {
    setCurrentChatContextId(contextId);
    setActiveTab(contextId || 'default');
    
    if (contextId === null) {
      console.log('Cleared chat context selection - using default chat');
    }
  };

  // Truncate long tab names
  const truncateTabName = (name, maxLength = 15) => {
    return name.length > maxLength ? `${name.substring(0, maxLength)}...` : name;
  };

  return {
    activeTab,
    setActiveTab,
    chatContexts,
    setChatContexts,
    currentChatContextId,
    setCurrentChatContextId,
    handleTabChange,
    handleCreateNewTab,
    handleCreateChatContext,
    handleUpdateChatContext,
    handleEditTab,
    handleDeleteChatContext,
    handleDeleteTab,
    handleCloseTab,
    handleSelectChatContext,
    truncateTabName,
  };
};

export default useChatTabs;
